import { Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import LeoChat from "./LeoChat";
import SupportChat from "./SupportChat";
import SiteSchema from "./SiteSchema";
import CookieBanner from "./CookieBanner";
import StickyCTA from "./StickyCTA";

const LUMORA_HREFS = [
  "/lumora/css/normalize.css",
  "/lumora/css/webflow.css",
  "/lumora/css/kalyans-supercool-site-d033bd.webflow.css",
  "/lumora/lumora-extra.css",
  "/jumbo/css/gc-new-jumbo-website.webflow.css",
  "/jumbo/boss-extra.css",
  "https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Hanken+Grotesk:wght@400;500;600;700;800&family=Hind:wght@300;400;500;600;700&display=swap",
  "https://api.fontshare.com/v2/css?f[]=satoshi@400,500,700,800,900&display=swap",
  "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.7.2/css/all.min.css",
];

// The local stylesheets that must be applied before the page is shown,
// otherwise unstyled markup flashes ("FOUC") before the CSS loads.
const CRITICAL = new Set([
  "/lumora/css/normalize.css",
  "/lumora/css/webflow.css",
  "/lumora/css/kalyans-supercool-site-d033bd.webflow.css",
  "/lumora/lumora-extra.css",
  "/jumbo/css/gc-new-jumbo-website.webflow.css",
  "/jumbo/boss-extra.css",
]);

/** Inject the purchased Lumora template stylesheets only while public pages
 * are mounted. Admin uses a separate layout, so it stays unaffected.
 * Returns `ready` once the critical stylesheets have loaded so we can gate
 * rendering and avoid a flash of unstyled content. */
function useLumoraStyles() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let loaded = 0;
    const total = LUMORA_HREFS.filter((h) => CRITICAL.has(h)).length;
    const markLoaded = () => {
      loaded += 1;
      if (loaded >= total) setReady(true);
    };
    const links = LUMORA_HREFS.map((href) => {
      const l = document.createElement("link");
      l.rel = "stylesheet";
      l.href = href;
      l.setAttribute("data-lumora", "1");
      if (CRITICAL.has(href)) {
        l.addEventListener("load", markLoaded);
        l.addEventListener("error", markLoaded);
      }
      document.head.appendChild(l);
      return l;
    });
    document.body.setAttribute("data-lumora", "1");
    // Safety net: never keep the page hidden for more than 1.2s.
    const fallback = setTimeout(() => setReady(true), 1200);
    return () => {
      links.forEach((l) => l.remove());
      document.body.removeAttribute("data-lumora");
      clearTimeout(fallback);
    };
  }, []);
  return ready;
}

export default function PublicLayout() {
  const loc = useLocation();
  const ready = useLumoraStyles();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [loc.pathname]);

  // Capture an inbound referral code (?ref=CODE) and remember it for the
  // visitor's eventual submission.
  useEffect(() => {
    const code = new URLSearchParams(loc.search).get("ref");
    if (code) {
      try { localStorage.setItem("gc_ref", code.trim().toUpperCase()); } catch { /* ignore */ }
    }
  }, [loc.search]);

  return (
    <div
      className="min-h-screen flex flex-col"
      data-testid="public-layout"
      style={{ opacity: ready ? 1 : 0, transition: "opacity .2s ease" }}
    >
      <SiteSchema />
      <div className="announcement-wrapper" data-testid="announcement-bar">
        <div className="announcement-text">
          <span className="announcement">Secure</span> Every eligible gift card is reviewed and verified by our team before payout.
        </div>
      </div>
      <Navbar />
      <main className="flex-1 lm-public-main">
        <Outlet />
      </main>
      <Footer />
      <LeoChat />
      <SupportChat />
      <CookieBanner />
      <StickyCTA />
    </div>
  );
}
